/**
 * The sky — the first one in the game, and it is a painted one.
 *
 * Spec §2 asks for a *"painted-looking gradient skybox, never a photo HDRI"*, so
 * this bakes one: a small equirectangular `DataTexture` assigned to
 * `scene.background`. Zero draw calls, zero lights, one texture built once at
 * zone load. A dome mesh would have been the other option and it would have cost
 * a draw call, a material and a radius that has to stay inside `camera.far` —
 * for a gradient that never moves, all three are a bill with nothing behind it.
 *
 * **It renders the palette; it does not restate it.** Every colour here comes
 * from the `ZonePalette` that was handed in — zenith from `shadow`, the middle
 * from `fill`, the horizon from `fog` — which is the same rule the mood board
 * keeps. The horizon being the zone's fog colour is what makes distant geometry
 * dissolve *into* the sky rather than in front of it; if the two are ever
 * authored apart, the far side of the yard grows a visible seam at the skyline.
 *
 * **The flash is not here.** `storm.flash` reaches the frame through the light
 * rig (which really does raise the irradiance, so surfaces climb the toon ramp)
 * and through the post stack (which lifts the whole image toward the zone's
 * accent). A sky that brightened on its own would be a fourth author of the same
 * event, and beat 6's rule 1 is that the flash the player sees and the flash the
 * stealth system used are one number. See `docs/beat-6-plan.md`.
 */

import * as THREE from 'three';
import { mulberry32 } from '@/audio/prng';
import type { ZonePalette } from '@/render/palette';

/**
 * Texture size. Small on purpose.
 *
 * The content is a vertical gradient with soft horizontal strata across it —
 * there is nothing in it that survives being sampled at higher resolution, and
 * the bilinear filter is doing most of the painting either way. 128×64 is 32 kB.
 */
const WIDTH = 128;
const HEIGHT = 64;

/** How far up the sky the horizon band reaches, 0 at the zenith, 1 at the bottom. */
const HORIZON = 0.62;

export interface SkyOptions {
  /**
   * Seed for the cloud strata. Authored per room, never sampled.
   *
   * D-045: the simulation must replay, and a screenshot gate that compares two
   * runs of the same room is worthless if its sky is different each time.
   */
  readonly seed?: number;
  /**
   * How strongly the strata break up the gradient, 0..1. Defaults to 0.35.
   *
   * At 0 this is a clean gradient, which is what an interior with a skylight
   * would want. The storm wants it high enough that the sky has weather in it
   * and low enough that nothing in it reads as a shape — a cloud with an edge
   * becomes a thing the player looks at, and the only thing worth looking at up
   * there is the lightning.
   */
  readonly strata?: number;
  /**
   * Override for the zenith colour, sRGB hex. Defaults to the zone's `shadow`.
   *
   * ── Why this exists, and the frame is what asked for it ──────────────────────
   *
   * The default mapping — zenith from `shadow`, middle from `fill`, horizon from
   * `fog` — is right for every zone whose `shadow` is the darkest thing in a
   * ROOM, which is all of them indoors and is the yard as well (`0x121a29`, a
   * night blue).
   *
   * **It is wrong for `outside`, whose `shadow` is `0x3a5742` — a dark GREEN**,
   * because in a field the deepest value in frame is shadow on grass. Baked
   * straight in, it gave beat 7's bloom a flat blue-grey sky with a green cast at
   * the top and no dawn in it at all, in the one shot the whole act walks toward.
   *
   * The right zenith for that zone is a deep version of its own `fill`, which is
   * the sky's colour by definition (`ZonePalette.fill` is *"bounce and sky
   * fill"*). Passing it in keeps this function rendering the palette rather than
   * restating it — a caller may darken a palette value, and may not invent one.
   */
  readonly zenith?: number;
  /**
   * Override for the colour AT the horizon line, sRGB hex. Defaults to the
   * zone's `fill`.
   *
   * The camera's far plane is 100 m, so an exterior's ground always ends there,
   * hazed to the zone's `fog`, and meets the sky at the horizon line — where
   * the default puts `fill`. Where those two are close (beat 7's bloom) there
   * is no seam. Beat 12's dawn has a pale warm `fog` and a dark cold `fill`,
   * and the first frames showed a ruled line where the field stopped: pale
   * ground under a flat navy sky. Passing `fog` here makes the ground dissolve
   * into the sky, and it is also the plan's "one warm band at the horizon".
   * A palette value, as `zenith` is: a caller may choose one, not invent one.
   */
  readonly middle?: number;
}

export interface Sky {
  /** Assign to `scene.background`. */
  readonly background: THREE.Texture;
  dispose(): void;
}

/** Smooth 1-D value noise from a seeded table, so the strata are painted, not banded. */
function stratum(table: readonly number[], t: number): number {
  const scaled = t * table.length;
  const index = Math.floor(scaled);
  const fraction = scaled - index;
  const a = table[((index % table.length) + table.length) % table.length] ?? 0;
  const b = table[(((index + 1) % table.length) + table.length) % table.length] ?? 0;
  const smooth = fraction * fraction * (3 - 2 * fraction);
  return a + (b - a) * smooth;
}

export function createSky(zone: ZonePalette, options: SkyOptions = {}): Sky {
  const random = mulberry32(options.seed ?? 0x5eed);
  const strataAmount = options.strata ?? 0.35;

  const zenith = new THREE.Color(options.zenith ?? zone.shadow);
  const middle = new THREE.Color(options.middle ?? zone.fill);
  const horizon = new THREE.Color(zone.fog);

  // Two tables at different rates. One alone gives evenly spaced bands, which
  // reads as a gradient with a bug in it rather than as cloud.
  const coarse = Array.from({ length: 6 }, () => random() - 0.5);
  const fine = Array.from({ length: 17 }, () => random() - 0.5);

  const data = new Uint8Array(WIDTH * HEIGHT * 4);
  const colour = new THREE.Color();

  for (let y = 0; y < HEIGHT; y += 1) {
    // v = 0 at the top row. An equirect background maps row 0 to the zenith.
    const v = (y + 0.5) / HEIGHT;
    if (v < HORIZON) {
      colour.copy(zenith).lerp(middle, v / HORIZON);
    } else {
      colour.copy(middle).lerp(horizon, (v - HORIZON) / (1 - HORIZON));
    }

    // Strata fade out at the zenith and at the horizon: overhead there is
    // nothing to catch the light, and at the horizon the haze has already
    // dissolved everything. Both ends being clean is what stops the bands
    // reading as a texture wrapped around a ball.
    const shaping = Math.sin(Math.PI * Math.min(1, v / HORIZON)) * strataAmount;

    for (let x = 0; x < WIDTH; x += 1) {
      const u = (x + 0.5) / WIDTH;
      // Sampled on u as well as v so the strata are not perfectly horizontal
      // stripes; the u term is deliberately slow, a whole sky is two of them.
      const n =
        stratum(coarse, v * 0.9 + u * 0.2) * 0.65 + stratum(fine, v * 2.3 + u * 0.35) * 0.35;
      const lift = 1 + n * shaping;

      const offset = (y * WIDTH + x) * 4;
      data[offset] = Math.round(THREE.MathUtils.clamp(colour.r * lift, 0, 1) * 255);
      data[offset + 1] = Math.round(THREE.MathUtils.clamp(colour.g * lift, 0, 1) * 255);
      data[offset + 2] = Math.round(THREE.MathUtils.clamp(colour.b * lift, 0, 1) * 255);
      data[offset + 3] = 255;
    }
  }

  const texture = new THREE.DataTexture(data, WIDTH, HEIGHT, THREE.RGBAFormat);
  // Equirectangular, so three's background shader maps it by view direction and
  // the sky stays put when the spring arm swings. A plain texture background is
  // stretched to the viewport instead and slides with the camera, which reads as
  // a painted backdrop on a trolley.
  texture.mapping = THREE.EquirectangularReflectionMapping;
  // The palette is authored in sRGB hex like every other colour in the project,
  // and the post stack does the final encode itself. Declaring the space here is
  // what keeps the sky from being the one surface in the frame that is graded
  // twice.
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.minFilter = THREE.LinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  texture.needsUpdate = true;

  return {
    background: texture,
    dispose(): void {
      texture.dispose();
    },
  };
}
